/**
 * Name: Tabs
 * Description: Accessible tab component with smooth fade & slide transitions
 */

import Component from "@lunaweb/vega-reactor-js/src/patterns/Component";

export default class Tabs extends Component {
  static settings = {
    button: ".js-tabs-button",
    content: ".js-tabs-content",
    activeClass: "is-active",
    enteringClass: "is-entering",
    leavingClass: "is-leaving",
    transitionDuration: 200,
  };

  #buttons;
  #contents;
  #handleClick;

  constructor(element, options = {}) {
    super(element, { ...Tabs.settings, ...options });
    this.#buttons = this.element.querySelectorAll(this.settings.button);
    this.#contents = this.element.querySelectorAll(this.settings.content);
    this.#handleClick = this._handleClick.bind(this);
  }

  _handleClick(event) {
    const button = event.currentTarget;
    const targetId = button.getAttribute("data-tab");
    const targetContent = this.element.querySelector(`#${targetId}`);

    if (!targetContent) return;

    // Éviter le clic si déjà actif
    if (button.classList.contains(this.settings.activeClass)) return;

    this.#buttons.forEach((btn) =>
      btn.classList.remove(this.settings.activeClass)
    );
    button.classList.add(this.settings.activeClass);

    const currentContent = Array.from(this.#contents).find((c) =>
      c.classList.contains(this.settings.activeClass)
    );

    if (currentContent) {
      this._fadeOut(currentContent, () => this._fadeIn(targetContent));
    } else {
      this._fadeIn(targetContent);
    }

    this.fire("change", { button, targetContent });
  }

  _fadeIn(element) {
    element.classList.remove(this.settings.leavingClass);
    element.classList.add(this.settings.enteringClass);
    element.classList.add(this.settings.activeClass);

    // forcer le reflow avant d'animer
    void element.offsetWidth;

    element.style.transitionDelay = "50ms"; // petit délai pour enchaîner naturellement

    setTimeout(() => {
      element.classList.remove(this.settings.enteringClass);
      element.style.transitionDelay = "";
    }, this.settings.transitionDuration);
  }

  _fadeOut(element, callback) {
    element.classList.add(this.settings.leavingClass);
    element.classList.remove(this.settings.activeClass);

    setTimeout(() => {
      element.classList.remove(this.settings.leavingClass);
      if (callback) callback();
    }, this.settings.transitionDuration);
  }

  mount() {
    this.#buttons.forEach((button) =>
      button.addEventListener("click", this.#handleClick)
    );

    if (this.#buttons.length && this.#contents.length) {
      this.#buttons[0].classList.add(this.settings.activeClass);
      this.#contents[0].classList.add(this.settings.activeClass);
    }

    super.mount();
  }

  unmount() {
    this.#buttons.forEach((button) =>
      button.removeEventListener("click", this.#handleClick)
    );
    super.unmount();
  }
}
